import {ChatMessagesGroup} from '@github-ui/copilot-chat/components/ChatMessagesGroup'
import {ChatScrollContainer, useChatScroll} from '@github-ui/copilot-chat/components/ChatScrollContainer'
import {MessagesPortalContainer} from '@github-ui/copilot-chat/components/PortalContainerUtils'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {useChat} from '@github-ui/copilot-chat/hooks/use-chat'
import {useGroupedMessages} from '@github-ui/copilot-chat/hooks/use-grouped-messages'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import {useSSOOrgNames} from '@github-ui/copilot-chat/hooks/use-sso-org-names'
import {
  PREMIUM_QUOTA_LOW_TRIGGER,
  threadName as getThreadName,
} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {getActiveMessages} from '@github-ui/copilot-chat/utils/copilot-chat-subthreading-helpers'
import {NullMessageId} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useNavigableRef} from '@github-ui/navigable-ref'
import {usePreviousValue} from '@github-ui/use-previous-value'
import {ArrowDownIcon} from '@primer/octicons-react'
import {IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useDeferredValue, useEffect, useLayoutEffect} from 'react'

import {useIsSharedThread} from '../hooks/use-is-shared-thread'
import {ChatMessage} from './ChatMessage'
import {ConversationLoader} from './ConversationLoader'
import {ErrorState, SharedThreadForbidden} from './ErrorState'
import styles from './ImmersiveChat.module.css'

interface ImmersiveChatProps {
  inputRef?: React.RefObject<HTMLTextAreaElement | null>
  isMobile: boolean
}

const Messages = ({inputRef, isMobile}: ImmersiveChatProps) => {
  const {scrollToBottom} = useChatScroll()
  const messagesState = useChatStateValues(
    'isWaitingOnCopilot',
    'messagesLoading',
    'streamingMessage',
    'messages',
    'selectedThreadID',
    'threads',
    'pendingFirstMessage',
    'threadHasNewMessages',
  )
  const {
    isWaitingOnCopilot,
    messagesLoading,
    streamingMessage,
    messages: completedMessages,
    threadHasNewMessages,
    pendingFirstMessage,
  } = messagesState
  const {reloadQuota, premiumChatQuotaRemaining, isLicensedLimited} = useEntitlement()

  const thread = getSelectedThread(messagesState)
  const threadName = getThreadName(thread)
  const sharedMessageId = thread?.sharedMessageID

  const {messages, groupedMessages} = useGroupedMessages(messagesState)

  // This renders the list from deferred copies of the data. On submit, React paints the cheap,
  // important updates first, then renders the heavy message list in a lower-priority pass.
  // groupedMessages, the active messages, and completedMessages all come from the same state
  // update, so deferring them together keeps them in sync with each other.
  //
  // threadHasNewMessages and pendingFirstMessage decide whether the last group fills the screen
  // and scrolls (see ChatMessagesGroup). Defer them too so that decision happens in the same pass
  // as the list. Otherwise the previous group could briefly fill the screen before the deferred
  // list catches up.
  const renderedGroupedMessages = useDeferredValue(groupedMessages)
  const renderedMessages = useDeferredValue(messages)
  const renderedCompletedMessages = useDeferredValue(completedMessages)
  const renderedThreadHasNewMessages = useDeferredValue(threadHasNewMessages)
  const renderedPendingFirstMessage = useDeferredValue(pendingFirstMessage)
  const firstReplyId = renderedMessages.find(message => message.role === 'assistant')?.id

  // Instant scroll to bottom on mount
  useLayoutEffect(() => scrollToBottom('instant'), [scrollToBottom])

  const prevStreamingMessage = usePreviousValue(streamingMessage)

  useEffect(() => {
    if (
      messages.length > 0 &&
      !isLicensedLimited &&
      copilotFeatureFlags.premiumRequestQuotasEnabled &&
      premiumChatQuotaRemaining <= PREMIUM_QUOTA_LOW_TRIGGER &&
      !isWaitingOnCopilot &&
      messagesLoading.state === 'loaded' &&
      !!prevStreamingMessage &&
      !streamingMessage
    ) {
      // make sure we reload the quota if the user is close to exceeding it
      reloadQuota('quota_low_trigger')
    }
  }, [
    isLicensedLimited,
    isWaitingOnCopilot,
    messages.length,
    messagesLoading.state,
    premiumChatQuotaRemaining,
    prevStreamingMessage,
    reloadQuota,
    streamingMessage,
  ])

  return (
    <div>
      <div className="sr-only">
        <h1>Copilot Chat</h1>
        <h2>{threadName}</h2>
      </div>
      {renderedGroupedMessages.map((group, i) => {
        const isLastGroup = i === renderedGroupedMessages.length - 1
        // Compute this from the deferred values above so the fill/scroll decision matches the list
        // being shown. ChatMessagesGroup has the full reason this prop exists.
        const fillViewport = isLastGroup && (renderedThreadHasNewMessages || Boolean(renderedPendingFirstMessage))
        return (
          // `message.id` is not a stable identifier because a temporary message is created locally while we
          // wait for CAPI. The ID of this message isn't the same as the message that is returned by CAPI.
          // Messages within a conversation cannot be reordered so the index should be safe to use.
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <ChatMessagesGroup key={i} isLastGroup={isLastGroup} fillViewport={fillViewport}>
            {group.map((message, j) => {
              // -1 indicates a streaming message; assign it to what it will become
              // otherwise, use the messageIndex, which will always be defined by this point
              const messageIndex =
                message.messageIndex === -1 ? renderedCompletedMessages.length : message.messageIndex!
              const isLatestMessage = isLastGroup && j === group.length - 1
              return (
                <ChatMessage
                  // Using `j` for the key goes against React's "best practice" of using unique keys,
                  // but it's necessary for accessibility. In order to keep keyboard focus on the action bar when
                  // switching subthreads, we can't re-create it, which happens if React thinks it needs to re-create
                  // the entire ChatMessage (rather than specific components within).
                  //
                  // Messages within a conversation cannot be reordered so the index should be safe to use.
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={j}
                  isLatestMessage={isLatestMessage}
                  isSharedMessage={message.id === sharedMessageId}
                  isFirstReply={message.id === firstReplyId}
                  message={message}
                  messageIndex={messageIndex}
                  inputRef={inputRef}
                  // Decide "is this the streaming reply?" by message id, not by `isLatestMessage &&
                  // isWaitingOnCopilot`. When the list is deferred it renders one step behind, so
                  // `isWaitingOnCopilot` can turn true before the new list shows up. The old check
                  // would then briefly tag the previous reply as still streaming and hide its action
                  // bar. A message's id is stable, so comparing ids stays correct while the list
                  // catches up.
                  isStreaming={message.id === streamingMessage?.id || message.id === NullMessageId}
                  autoOpenPreviewPane={!isMobile}
                />
              )
            })}
          </ChatMessagesGroup>
        )
      })}
    </div>
  )
}

const FailedToLoadThreadError = () => {
  const {mode} = useChatStateValues('mode')

  const errorMessage = 'Chat failed to load'

  useEffect(() => {
    sendEvent('dotcom_chat.activate.error', {
      target: 'ERROR_CONVERSATION_FAILED_TO_LOAD',
      mode,
      message: errorMessage,
    })
  }, [mode])

  return (
    <ErrorState
      title={errorMessage}
      description={
        <>
          <Link
            href=""
            onClick={e => {
              e.preventDefault()
              window.location.reload()
            }}
            inline
          >
            Reload the page
          </Link>{' '}
          to try again.
        </>
      }
    />
  )
}

const AccessDeniedError = ({ssoOrgNames}: {ssoOrgNames?: string[]}) => {
  const {mode} = useChatStateValues('mode')
  const errorMessage = 'Access denied'

  useEffect(() => {
    sendEvent('dotcom_chat.activate.error', {
      target: 'ERROR_ACCESS_DENIED',
      mode,
      message: errorMessage,
    })
  }, [mode])

  return <ErrorState title={errorMessage} description="Single sign-on to view this chat." ssoOrgs={ssoOrgNames} />
}

const NotFoundError = ({isSharedThread}: {isSharedThread: boolean}) => {
  const {mode} = useChatStateValues('mode')
  const selectedCopilotSpaceId = useSelectedCopilotSpaceId()
  const title = isSharedThread ? 'Shared chat not found' : 'Chat not found'
  const message = `This URL may be incorrect or the ${selectedCopilotSpaceId ? 'space' : 'chat'} may have been deleted.`

  useEffect(() => {
    sendEvent('dotcom_chat.activate.error', {
      target: isSharedThread ? 'ERROR_SHARED_CONVERSATION_NOT_FOUND' : 'ERROR_CONVERSATION_NOT_FOUND',
      mode,
      message: title,
    })
  }, [mode, isSharedThread, title])

  return <ErrorState title={title} description={message} />
}

export const ImmersiveChat = ({inputRef, isMobile}: ImmersiveChatProps) => {
  const state = useChatStateValues(
    'messagesLoading',
    'threadsLoading',
    'messages',
    'showTopicPicker',
    'pendingFirstMessage',
    'selectedThreadID',
    'threads',
    'fetchSharedThreads',
    'mode',
  )
  const {messagesLoading, threadsLoading, messages: loadedMessages, showTopicPicker, pendingFirstMessage} = state
  const thread = getSelectedThread(state)
  const {textAreaRef} = useChat(inputRef)
  const isSharedThread = useIsSharedThread()
  const {ssoOrgNames} = useSSOOrgNames()

  let messageContent = null
  let emptyContent = null

  const messages = getActiveMessages(loadedMessages, pendingFirstMessage)

  if (messages.length && messagesLoading.state === 'loaded') {
    messageContent = <Messages inputRef={textAreaRef} isMobile={isMobile} />
  } else if (
    (messagesLoading.state === 'loading' || messagesLoading.state === 'pending') &&
    state.selectedThreadID &&
    !showTopicPicker
  ) {
    messageContent = <ConversationLoader />
  } else if (
    messagesLoading.state === 'error' &&
    !!state.selectedThreadID &&
    ((thread === null && threadsLoading.state !== 'error') || messagesLoading.notFound || messagesLoading.notAuthorized)
  ) {
    if (
      isSharedThread &&
      state.fetchSharedThreads?.ok === false &&
      (state.fetchSharedThreads.status === 403 || state.fetchSharedThreads.status === 401)
    ) {
      emptyContent = <SharedThreadForbidden mode={state.mode} />
    } else if (messagesLoading.notAuthorized) {
      emptyContent = <AccessDeniedError ssoOrgNames={ssoOrgNames} />
    } else {
      emptyContent = <NotFoundError isSharedThread={isSharedThread} />
    }
  } else if (messagesLoading.state === 'error' || threadsLoading.state === 'error') {
    emptyContent = threadsLoading.status === 403 ? <AccessDeniedError /> : <FailedToLoadThreadError />
  }

  const containerRef = useNavigableRef<HTMLDivElement>()

  const isAssistive = state.mode === 'assistive'
  return (
    <ChatScrollContainer
      className={clsx(styles.container, isAssistive && styles.assistive)}
      containerRef={containerRef}
    >
      <ScrollToBottomButton />
      {emptyContent && <div className={styles.emptyContent}>{emptyContent}</div>}
      {messageContent && (
        <div className={styles.messageContent}>
          {messageContent}
          <MessagesPortalContainer />
        </div>
      )}
    </ChatScrollContainer>
  )
}

const ScrollToBottomButton = () => {
  const {isScrolledUp, scrollToBottom} = useChatScroll()

  return (
    <IconButton
      aria-label="Scroll to bottom"
      className={clsx(styles.scrollToBottomButton, !isScrolledUp && styles.hidden)}
      onClick={() => scrollToBottom('smooth')}
      icon={ArrowDownIcon}
      tooltipDirection="n"
    />
  )
}
